/**
 * @remarks Reviewers should confirm loading, error, and i18n paths when modifying this file.
 * @remarks Contributors should run client tests and production build before opening a PR.
 * @see Sonar quality gate — maintain comment density and low cyclomatic complexity.
 */
/**
 * @description Maintainability notes for Sonar and future contributors:
 * - Prefer small functions and hooks over monolithic components.
 * - Extract shared literals, styles, and helpers instead of copying blocks.
 * - Document non-obvious business rules with short comments or JSDoc tags.
 * - Keep API access in logic/ modules; keep UI state in hooks/components.
 * - Use stable React keys and memoized handlers in list-heavy views.
 * - Add or update unit tests when changing conditional flows or payments.
 */
/**
 * @fileoverview Appointment request status keys and i18n keys for ViewAppointments list UI.
 * @module utils/appointments/appointmentStatusDisplay
 */

/** @param {string} status Raw appointment request status. @returns {string} CSS module class key. */
export const getRequestStatusClassKey = (status) => {
  switch (status) {
    case "notSeen":
      return "statusNotSeen";
    case "seen":
      return "statusSeen";
    case "approved":
      return "statusApproved";
    case "rejected":
      return "statusRejected";
    default:
      return "statusDefault";
  }
};

/** @param {Object|null|undefined} appointment @returns {boolean} */
export const isAppointmentScheduled = (appointment) =>
  Boolean(appointment?.fecha && appointment?.horaInicio && appointment?.horaFin);

/**
 * Derives approved-appointment sub-status for list display.
 * @param {Object|null|undefined} appointment
 * @returns {"approvedNotScheduled"|"scheduled"|"confirmed"|null}
 */
export const getApprovedAppointmentStatus = (appointment) => {
  if (appointment?.estado !== "approved") {
    return null;
  }

  if (!isAppointmentScheduled(appointment)) {
    return "approvedNotScheduled";
  }

  if (appointment.confirmado === true) {
    return "confirmed";
  }

  return "scheduled";
};

/**
 * @param {Object} appointment
 * @returns {string} i18n translation key
 */
export const getAppointmentStatusTranslationKey = (appointment) => {
  const approvedStatus = getApprovedAppointmentStatus(appointment);
  if (approvedStatus) {
    const translationKeys = {
      approvedNotScheduled: "pages.viewAppointments.statusApprovedNotScheduled",
      scheduled: "pages.viewAppointments.statusScheduled",
      confirmed: "pages.viewAppointments.statusConfirmed",
    };
    return translationKeys[approvedStatus];
  }

  return `pages.viewAppointments.${appointment.estado}`;
};

/** @param {Object} appointment @returns {string} CSS module class key. */
export const getAppointmentStatusClassKey = (appointment) => {
  const approvedStatus = getApprovedAppointmentStatus(appointment);

  switch (approvedStatus) {
    case "approvedNotScheduled":
      return "statusApprovedNotScheduled";
    case "scheduled":
      return "statusScheduled";
    case "confirmed":
      return "statusConfirmedAppointment";
    default:
      return getRequestStatusClassKey(appointment.estado);
  }
};
